import React from "react";

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : null;

const toIndianFormat = (num) => {
  if (!num && num !== 0) return "";
  const str = String(Math.floor(Number(num)));
  if (str.length <= 3) return str;
  const last3 = str.slice(-3);
  const rest = str.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + last3;
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toUpperCase();
  const styles = {
    APPROVED: {
      background: "#eaf3de",
      color: "#3b6d11",
      border: "0.5px solid #c0dd97",
    },
    REJECTED: {
      background: "#fcebeb",
      color: "#a32d2d",
      border: "0.5px solid #f7c1c1",
    },
    PENDING: {
      background: "#faeeda",
      color: "#854f0b",
      border: "0.5px solid #fac775",
    },
  };
  const st = styles[s] || styles.PENDING;
  const icon = s === "APPROVED" ? "✓" : s === "REJECTED" ? "✗" : "…";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 500,
        padding: "3px 9px",
        borderRadius: 20,
        ...st,
      }}
    >
      {icon}{" "}
      {s === "APPROVED"
        ? "Approved"
        : s === "REJECTED"
          ? "Rejected"
          : "Pending"}
    </span>
  );
};

const Field = ({ label, value }) => {
  if (!value && value !== 0) return null;
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          color: "#9ca3af",
          marginBottom: 2,
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, color: "#111827" }}>{value}</div>
    </div>
  );
};

const InfoCard = ({ children }) => (
  <div
    style={{
      background: "#f9fafb",
      borderRadius: 8,
      padding: "10px 12px",
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 500,
      color: "#9ca3af",
      letterSpacing: "0.6px",
      marginBottom: 10,
    }}
  >
    {children}
  </div>
);

const Divider = () => (
  <div
    style={{ height: "0.5px", background: "rgba(0,0,0,0.1)", margin: "14px 0" }}
  />
);

const VacancyDetailsDrawer = ({ vacancy: v, onClose, onCreateJobPost }) => {
  if (!v) return null;

  const hodStatus = (
    v.HODApprovalStatus ||
    v.hodapprovalStatus ||
    ""
  ).toUpperCase();
  const hrStatus = (
    v.HRApprovalStatus ||
    v.hrapprovalStatus ||
    ""
  ).toUpperCase();
  const isApproved = (v.vacancyStatus || "").toUpperCase() === "APPROVED";
  const hasJobPost = !!v.jobPostId;

  const skills = v.skills
    ? v.skills
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const address = [
    v.branchAddressLine1,
    v.branchAddressLine2,
    v.branchCity,
    v.branchState,
    v.branchCountry,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 999,
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          height: "100%",
          width: 520,
          background: "#fff",
          borderLeft: "0.5px solid rgba(0,0,0,0.12)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "18px 20px 14px",
            borderBottom: "0.5px solid rgba(0,0,0,0.1)",
          }}
        >
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 500, color: "#111827" }}>
              {v.jobTitle}
            </h2>
            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Vacancy ID #{v.vacancyId}&nbsp;·&nbsp;{v.departmentName}
              &nbsp;·&nbsp;{v.branchName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              border: "0.5px solid rgba(0,0,0,0.2)",
              borderRadius: 8,
              background: "#f9fafb",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {/* HOD + HR status row */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#9ca3af",
                padding: "2px 7px",
                background: "#f3f4f6",
                borderRadius: 4,
              }}
            >
              HOD
            </span>
            <StatusBadge status={hodStatus || "PENDING"} />
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#9ca3af",
                padding: "2px 7px",
                background: "#f3f4f6",
                borderRadius: 4,
              }}
            >
              HR
            </span>
            <StatusBadge status={hrStatus || "PENDING"} />
          </div>

          {/* Vacancy details */}
          <SectionTitle>Vacancy details</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "10px 16px",
              marginBottom: 14,
            }}
          >
            <Field label="Positions" value={v.noOfVacancy} />
            <Field
              label="Employment type"
              value={v.employmentType?.replace(/_/g, " ")}
            />
            <Field
              label="Budget"
              value={
                v.budget
                  ? `₹${toIndianFormat(v.budget)} / ${(v.budgetType || "").toLowerCase()}`
                  : null
              }
            />
            <Field label="Role" value={v.roleName} />
            <Field label="Department" value={v.departmentName} />
            <Field label="Branch" value={v.branchName} />
            <Field
              label="Closing date"
              value={fmtDate(v.expectedClosingTime)}
            />
            <Field label="Requested by" value={v.vacancyRequestedEmpName} />
            <Field
              label="Job post ID"
              value={v.jobPostId ? `#${v.jobPostId}` : null}
            />
          </div>

          <Divider />

          {/* Job details */}
          <SectionTitle>Job details</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px 16px",
              marginBottom: 10,
            }}
          >
            <Field label="Experience" value={v.experience} />
            <Field
              label="Qualification"
              value={
                Array.isArray(v.qualifications) && v.qualifications.length > 0
                  ? v.qualifications.map((q) => q.qualificationName).join(", ")
                  : null
              }
            />
          </div>

          {skills.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  color: "#9ca3af",
                  letterSpacing: "0.5px",
                  marginBottom: 5,
                }}
              >
                Skills
              </div>
              <div>
                {skills.map((s) => (
                  <span
                    key={s}
                    style={{
                      display: "inline-flex",
                      background: "#f3f4f6",
                      border: "0.5px solid rgba(0,0,0,0.1)",
                      borderRadius: 4,
                      padding: "2px 7px",
                      fontSize: 11,
                      color: "#6b7280",
                      margin: 2,
                    }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {v.jobDescription && (
            <InfoCard>
              <div
                style={{
                  fontSize: 10,
                  color: "#9ca3af",
                  letterSpacing: "0.5px",
                  marginBottom: 4,
                }}
              >
                Job description
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#374151",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {v.jobDescription}
              </div>
            </InfoCard>
          )}

          {(v.responsibilities || v.desiredProfile) && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {v.responsibilities && (
                <InfoCard>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#9ca3af",
                      letterSpacing: "0.5px",
                      marginBottom: 4,
                    }}
                  >
                    Responsibilities
                  </div>
                  <div
                    style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}
                  >
                    {v.responsibilities}
                  </div>
                </InfoCard>
              )}
              {v.desiredProfile && (
                <InfoCard>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#9ca3af",
                      letterSpacing: "0.5px",
                      marginBottom: 4,
                    }}
                  >
                    Desired profile
                  </div>
                  <div
                    style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}
                  >
                    {v.desiredProfile}
                  </div>
                </InfoCard>
              )}
            </div>
          )}

          {v.reasonOfVacancy && (
            <>
              <Divider />
              <SectionTitle>Reason for vacancy</SectionTitle>
              <InfoCard>
                <div
                  style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}
                >
                  {v.reasonOfVacancy}
                </div>
              </InfoCard>
            </>
          )}

          <Divider />

          {/* Remarks */}
          <SectionTitle>Remarks</SectionTitle>
          <div
            style={{
              background: "#f0fdf4",
              border: "0.5px solid #c0dd97",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#3b6d11",
                letterSpacing: "0.5px",
                marginBottom: 4,
              }}
            >
              HOD remark
              {v.actionUpdatedHodName && (
                <span
                  style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}
                >
                  — {v.actionUpdatedHodName}
                  {v.hodActionTime ? ` · ${fmtDateTime(v.hodActionTime)}` : ""}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                color: v.hodRemark ? "#374151" : "#9ca3af",
                fontStyle: v.hodRemark ? "normal" : "italic",
              }}
            >
              {v.hodRemark || "No remark added"}
            </div>
          </div>
          <div
            style={{
              background: "#eff6ff",
              border: "0.5px solid #b5d4f4",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#185fa5",
                letterSpacing: "0.5px",
                marginBottom: 4,
              }}
            >
              HR remark
              {v.actionUpdatedHrName && (
                <span
                  style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6 }}
                >
                  — {v.actionUpdatedHrName}
                  {v.hrActionTime ? ` · ${fmtDateTime(v.hrActionTime)}` : ""}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                color: v.hrRemark ? "#374151" : "#9ca3af",
                fontStyle: v.hrRemark ? "normal" : "italic",
              }}
            >
              {v.hrRemark || "No remark added"}
            </div>
          </div>

          <Divider />

          {/* Approval timeline */}
          <SectionTitle>Approval timeline</SectionTitle>
          <div style={{ marginLeft: 8 }}>
            {[
              {
                role: "HOD review",
                name: v.actionUpdatedHodName,
                time: v.hodActionTime || v.approvedOn,
                status: hodStatus,
                dotColor: "#639922",
                dotBorder: "#eaf3de",
              },
              {
                role: "HR review",
                name: v.actionUpdatedHrName,
                time: v.hrActionTime || v.approvedOn,
                status: hrStatus,
                dotColor: "#185fa5",
                dotBorder: "#e6f1fb",
              },
            ].map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: "8px 0 8px 16px",
                  borderLeft: "2px solid rgba(0,0,0,0.1)",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    position: "absolute",
                    left: -6,
                    top: 12,
                    background: item.name ? item.dotColor : "#d1d5db",
                    border: `2px solid ${item.name ? item.dotBorder : "#f3f4f6"}`,
                  }}
                />
                <div>
                  <div style={{ fontSize: 13, color: "#111827" }}>
                    {item.role} —{" "}
                    {item.status
                      ? item.status === "APPROVED"
                        ? "Approved"
                        : item.status === "REJECTED"
                          ? "Rejected"
                          : "Pending"
                      : "Pending"}
                  </div>
                  {item.name ? (
                    <div
                      style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}
                    >
                      by {item.name}
                      {item.time ? ` · ${fmtDateTime(item.time)}` : ""}
                    </div>
                  ) : (
                    <div
                      style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}
                    >
                      Awaiting action
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Branch address */}
          {address && (
            <>
              <Divider />
              <SectionTitle>Branch address</SectionTitle>
              <InfoCard>
                <div
                  style={{ fontSize: 12, color: "#374151", lineHeight: 1.7 }}
                >
                  {address}
                  {v.branchPincode ? ` — ${v.branchPincode}` : ""}
                </div>
              </InfoCard>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "0.5px solid rgba(0,0,0,0.1)",
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 14px",
              border: "0.5px solid rgba(0,0,0,0.2)",
              borderRadius: 8,
              background: "transparent",
              fontSize: 13,
              cursor: "pointer",
              color: "#374151",
            }}
          >
            Close
          </button>
          <div style={{ flex: 1 }} />
          {isApproved &&
            (hasJobPost ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  background: "#f0fdf4",
                  border: "0.5px solid #bbf7d0",
                  borderRadius: 8,
                  fontSize: 13,
                  color: "#15803d",
                  fontWeight: 500,
                }}
              >
                ✓ Job post created
              </span>
            ) : (
              <button
                onClick={onCreateJobPost}
                style={{
                  padding: "8px 18px",
                  border: "none",
                  borderRadius: 8,
                  background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <rect
                    x="1.5"
                    y="1.5"
                    width="10"
                    height="10"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M6.5 4v5M4 6.5h5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Create job post
              </button>
            ))}
        </div>
      </div>
    </>
  );
};

export default VacancyDetailsDrawer;
